const clampToPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const average = (values) => {
  const nums = values.filter((v) => Number.isFinite(v));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
};

const parseBloodPressure = (bpValue) => {
  if (!bpValue) return null;

  if (typeof bpValue === "string") {
    const match = bpValue.match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
    if (!match) return null;
    return { systolic: Number(match[1]), diastolic: Number(match[2]) };
  }

  if (typeof bpValue === "object") {
    const systolic = Number(bpValue?.systolic);
    const diastolic = Number(bpValue?.diastolic);
    if (!Number.isFinite(systolic) || !Number.isFinite(diastolic)) return null;
    return { systolic, diastolic };
  }

  return null;
};

const scoreBloodPressure = (bpValue) => {
  const bp = parseBloodPressure(bpValue);
  if (!bp) return null;

  const { systolic, diastolic } = bp;
  if (systolic <= 120 && diastolic <= 80) return 95;
  if (systolic <= 130 && diastolic <= 85) return 78;
  if (systolic <= 140 && diastolic <= 90) return 60;
  return 38;
};

const scoreHeartRate = (heartRateValue) => {
  const heartRate = Number(heartRateValue);
  if (!Number.isFinite(heartRate)) return null;
  if (heartRate >= 60 && heartRate <= 100) return 95;
  if (heartRate >= 50 && heartRate <= 110) return 74;
  return 45;
};

const scoreOxygen = (oxygenValue) => {
  const oxygen = Number(oxygenValue);
  if (!Number.isFinite(oxygen)) return null;
  if (oxygen >= 95) return 95;
  if (oxygen >= 92) return 70;
  return 42;
};

const scoreTemperature = (temperatureValue) => {
  const temp = Number(temperatureValue);
  if (!Number.isFinite(temp)) return null;
  if (temp >= 97 && temp <= 99.5) return 90;
  if (temp <= 100.4) return 72;
  return 44;
};

export const deriveVitalsScore = ({ bloodPressure, heartRate, oxygenLevel, temperature }) => {
  const raw = average([
    scoreBloodPressure(bloodPressure),
    scoreHeartRate(heartRate),
    scoreOxygen(oxygenLevel),
    scoreTemperature(temperature),
  ]);

  return clampToPercent(raw);
};

export const deriveQureoScore = ({
  summaryScore,
  assessmentScore,
  bloodPressure,
  heartRate,
  oxygenLevel,
  temperature,
}) => {
  const normalizedSummary = clampToPercent(summaryScore);
  const normalizedAssessment = clampToPercent(assessmentScore);
  const vitalsScore = deriveVitalsScore({ bloodPressure, heartRate, oxygenLevel, temperature });

  if (normalizedSummary != null) {
    const hasFallbackData = normalizedAssessment != null || vitalsScore != null;
    if (!(normalizedSummary === 0 && hasFallbackData)) {
      return normalizedSummary;
    }
  }

  if (normalizedAssessment != null && vitalsScore != null) {
    // Weight assessments slightly higher while still incorporating live remote-monitoring vitals.
    return clampToPercent((normalizedAssessment * 0.7) + (vitalsScore * 0.3));
  }

  if (normalizedAssessment != null) return normalizedAssessment;
  if (vitalsScore != null) return vitalsScore;
  return null;
};

export const getQureoScoreLabel = (score) => {
  const normalized = clampToPercent(score);
  if (normalized == null) return "No data";
  if (normalized >= 85) return "Excellent";
  if (normalized >= 70) return "Good";
  if (normalized >= 55) return "Fair";
  if (normalized >= 40) return "Needs Work";
  return "Poor";
};

export const getQureoHealthStatus = (score) => {
  const normalized = clampToPercent(score);
  if (normalized == null) return "No status";
  if (normalized >= 75) return "Healthy";
  if (normalized >= 60) return "Stable";
  if (normalized >= 45) return "Watch";
  return "Needs attention";
};
